import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CreditCard,
  PlusCircle,
  Trash2,
} from 'react-feather';

import Modal from '../../components/Modal';
import {
  addPaymentMethod,
  deletePaymentMethod,
  fetchPaymentCustomerCode,
  fetchPaymentMethods,
} from './BillingUtils';
import './billing.css';

const COUNTRY_OPTIONS = [
  { value: 'CAN', label: 'Canada' },
  { value: 'USA', label: 'United States' },
];

const initialAddForm = {
  cardholderName: '',
  cardNumber: '',
  expiryMonth: '',
  expiryYear: '',
  cvv: '',
  street1: '',
  city: '',
  province: '',
  postalCode: '',
  country: 'CAN',
  isDefault: false,
};

const getCardLabel = (card) => {
  const cardType = String(card?.card_type || '').trim();
  const lastFour = card?.last_four || '----';
  const isUnknownCardType = !cardType || cardType.toLowerCase() === 'unknown';

  if (isUnknownCardType) {
    return `**** **** **** ${lastFour}`;
  }

  return `${cardType} ending in ${lastFour}`;
};

const formatExpiry = (card) => {
  const rawMonth = String(card?.expiry_month || '');
  const year = String(card?.expiry_year || '');
  if (!rawMonth || !year) {
    return 'Expiry unavailable';
  }

  const month = rawMonth.padStart(2, '0');
  return `${month}/${year}`;
};

const normalizeYear = (value) => value.replace(/\D/g, '').slice(0, 4);

const normalizeMonth = (value) => value.replace(/\D/g, '').slice(0, 2);

const buildBillingAddressPayload = (formState) => ({
  name: formState.cardholderName.trim(),
  street_1: formState.street1.trim(),
  city: formState.city.trim(),
  province: formState.province.trim().toUpperCase(),
  postal_code: formState.postalCode.trim(),
  country: formState.country,
});

const AddPaymentMethodForm = ({
  customerCode,
  isSubmitting,
  onCancel,
  onSubmit,
}) => {
  const [formState, setFormState] = useState(initialAddForm);
  const [scriptReady, setScriptReady] = useState(false);
  const [isTokenizing, setIsTokenizing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setFormState(initialAddForm);
    setErrorMessage('');
    setScriptReady(false);

    if (!process.env.REACT_APP_HELCIM_JS_URL) {
      setErrorMessage('Helcim.js is not configured for this environment.');
      return undefined;
    }

    let isActive = true;
    const existingScript = document.querySelector(
      'script[data-helcim-context="billing-payment-method"]'
    );

    if (existingScript) {
      setScriptReady(typeof window.helcimProcess === 'function');
      return undefined;
    }

    const script = document.createElement('script');
    script.src = process.env.REACT_APP_HELCIM_JS_URL;
    script.async = true;
    script.dataset.helcimContext = 'billing-payment-method';

    script.onload = () => {
      if (isActive) {
        setScriptReady(typeof window.helcimProcess === 'function');
      }
    };

    script.onerror = () => {
      if (isActive) {
        setErrorMessage('Failed to load the payment form. Please refresh and try again.');
      }
    };

    document.body.appendChild(script);

    return () => {
      isActive = false;
      if (script.parentNode === document.body) {
        document.body.removeChild(script);
      }
      window.helcimCallbackResolve = null;
      window.helcimCallbackReject = null;
    };
  }, []);

  const handleChange = (field, value) => {
    setFormState((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (typeof window.helcimProcess !== 'function') {
      setErrorMessage('Payment gateway is still loading. Please try again in a moment.');
      return;
    }

    setIsTokenizing(true);

    try {
      const form = document.getElementById('helcimForm');
      const helcimResults = document.getElementById('helcimResults');

      if (!form || !helcimResults) {
        throw new Error('Payment form is not ready. Please reopen the dialog.');
      }

      form.submit = () => {
        window.setTimeout(() => {
          const response = helcimResults.querySelector('input[name="response"]')?.value;
          const responseMessage = helcimResults.querySelector('input[name="responseMessage"]')?.value;
          const cardToken = helcimResults.querySelector('input[name="cardToken"]')?.value;
          const domCustomerCode = helcimResults.querySelector('input[name="customerCode"]')?.value;
          const effectiveCustomerCode = domCustomerCode || customerCode || null;

          if (response === '1' && cardToken) {
            if (!effectiveCustomerCode) {
              window.helcimCallbackReject?.(
                new Error('Card verified, but no customer code was returned. Please try again.')
              );
              return;
            }

            window.helcimCallbackResolve?.({
              cardToken,
              customerCode: effectiveCustomerCode,
            });
          } else {
            window.helcimCallbackReject?.(
              new Error(responseMessage || 'Card verification failed.')
            );
          }
        }, 100);

        return false;
      };

      const helcimResponse = await new Promise((resolve, reject) => {
        window.helcimCallbackResolve = resolve;
        window.helcimCallbackReject = reject;
        window.helcimProcess();
      });

      await onSubmit({
        card_token: helcimResponse.cardToken,
        customer_code: helcimResponse.customerCode,
        cardholder_name: formState.cardholderName.trim(),
        billing_address: buildBillingAddressPayload(formState),
        is_default: formState.isDefault,
      });
    } catch (error) {
      setErrorMessage(error.message || 'Failed to add payment method.');
    } finally {
      setIsTokenizing(false);
      window.helcimCallbackResolve = null;
      window.helcimCallbackReject = null;
    }
  };

  const isBusy = isSubmitting || isTokenizing;

  return (
    <form id="helcimForm" name="helcimForm" onSubmit={handleSubmit}>
      <div id="helcimResults" />
      <input type="hidden" id="token" value={process.env.REACT_APP_HELCIM_JS_TOKEN || ''} />
      <input type="hidden" id="customerCode" value={customerCode || ''} />
      <input type="hidden" id="language" value="en" />
      <input type="hidden" id="cardHolderAddress" value={formState.street1} />
      <input type="hidden" id="cardHolderPostalCode" value={formState.postalCode} />

      <div className="payment-method-form-grid">
        <div className="form-group">
          <label htmlFor="cardHolderName">Name on Card</label>
          <input
            id="cardHolderName"
            name="cardHolderName"
            type="text"
            value={formState.cardholderName}
            onChange={(event) => handleChange('cardholderName', event.target.value)}
            required
            disabled={isBusy}
          />
        </div>

        <div className="form-group payment-method-form-full">
          <label htmlFor="cardNumber">Card Number</label>
          <input
            id="cardNumber"
            name="cardNumber"
            type="text"
            value={formState.cardNumber}
            onChange={(event) => handleChange('cardNumber', event.target.value)}
            required
            disabled={isBusy}
            inputMode="numeric"
          />
        </div>

        <div className="form-group">
          <label htmlFor="cardExpiryMonth">Expiry Month</label>
          <input
            id="cardExpiryMonth"
            name="cardExpiryMonth"
            type="text"
            placeholder="MM"
            value={formState.expiryMonth}
            onChange={(event) => handleChange('expiryMonth', normalizeMonth(event.target.value))}
            required
            disabled={isBusy}
            inputMode="numeric"
          />
        </div>

        <div className="form-group">
          <label htmlFor="cardExpiryYear">Expiry Year</label>
          <input
            id="cardExpiryYear"
            name="cardExpiryYear"
            type="text"
            placeholder="YY"
            value={formState.expiryYear}
            onChange={(event) => handleChange('expiryYear', normalizeYear(event.target.value))}
            required
            disabled={isBusy}
            inputMode="numeric"
          />
        </div>

        <div className="form-group">
          <label htmlFor="cardCVV">CVV</label>
          <input
            id="cardCVV"
            name="cardCVV"
            type="text"
            value={formState.cvv}
            onChange={(event) => handleChange('cvv', event.target.value.replace(/\D/g, '').slice(0, 4))}
            required
            disabled={isBusy}
            inputMode="numeric"
          />
        </div>

        <div className="form-group payment-method-form-full">
          <label htmlFor="billingStreet1">Billing Address</label>
          <input
            id="billingStreet1"
            type="text"
            value={formState.street1}
            onChange={(event) => handleChange('street1', event.target.value)}
            required
            disabled={isBusy}
          />
        </div>

        <div className="form-group">
          <label htmlFor="billingCity">City</label>
          <input
            id="billingCity"
            type="text"
            value={formState.city}
            onChange={(event) => handleChange('city', event.target.value)}
            required
            disabled={isBusy}
          />
        </div>

        <div className="form-group">
          <label htmlFor="billingProvince">Province / State</label>
          <input
            id="billingProvince"
            type="text"
            value={formState.province}
            onChange={(event) => handleChange('province', event.target.value.toUpperCase().slice(0, 2))}
            required
            disabled={isBusy}
            maxLength={2}
          />
        </div>

        <div className="form-group">
          <label htmlFor="billingPostalCode">Postal / ZIP</label>
          <input
            id="billingPostalCode"
            type="text"
            value={formState.postalCode}
            onChange={(event) => handleChange('postalCode', event.target.value)}
            required
            disabled={isBusy}
          />
        </div>

        <div className="form-group">
          <label htmlFor="billingCountry">Country</label>
          <select
            id="billingCountry"
            value={formState.country}
            onChange={(event) => handleChange('country', event.target.value)}
            disabled={isBusy}
          >
            {COUNTRY_OPTIONS.map((country) => (
              <option key={country.value} value={country.value}>
                {country.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <label className="payment-method-checkbox">
        <input
          type="checkbox"
          checked={formState.isDefault}
          onChange={(event) => handleChange('isDefault', event.target.checked)}
          disabled={isBusy}
        />
        <span>Set as my default card</span>
      </label>

      <p className="payment-method-form-note">
        This uses the same secure Helcim.js card tokenization flow as the rest of your billing setup.
      </p>

      {errorMessage && (
        <div className="payment-method-alert error" role="alert">
          <AlertCircle size={16} />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="payment-method-modal-actions">
        <button type="button" className="payment-method-secondary-btn" onClick={onCancel} disabled={isBusy}>
          Cancel
        </button>
        <button
          type="submit"
          className="payment-method-primary-btn"
          disabled={isBusy || !scriptReady}
        >
          {isBusy ? 'Saving...' : 'Save Card'}
        </button>
      </div>
    </form>
  );
};

// Note: We do not have edit functionality as we do not allow editing card details,
// since editing involves voiding the token. To update a card, the user must remove
// the old one and add a new card through the tokenization flow.

const PaymentMethods = ({ onStatusChange }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [customerCode, setCustomerCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [removingCard, setRemovingCard] = useState(null);
  const [modalSubmitting, setModalSubmitting] = useState(false);

  const token = useMemo(() => localStorage.getItem('access_token'), []);
  const publishStatus = useCallback((type, message) => {
    onStatusChange?.(message ? { type, message } : null);
  }, [onStatusChange]);

  const loadPaymentData = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [methods, code] = await Promise.all([
        fetchPaymentMethods(token),
        fetchPaymentCustomerCode(token),
      ]);

      setPaymentMethods(methods);
      setCustomerCode(code);
    } catch (error) {
      const message = error.message || 'Failed to load payment methods.';
      publishStatus('error', message);
    } finally {
      setLoading(false);
    }
  }, [publishStatus, token]);

  useEffect(() => {
    loadPaymentData();
  }, [loadPaymentData]);

  const handleMutationError = (error, fallbackMessage) => {
    const message = error.message || fallbackMessage;
    publishStatus('error', message);
    throw error;
  };

  const handleAddCard = async (payload) => {
    try {
      setModalSubmitting(true);
      await addPaymentMethod(token, payload);
      publishStatus('success', 'Payment method added successfully.');
      setAddModalOpen(false);
      await loadPaymentData();
    } catch (error) {
      handleMutationError(error, 'Failed to add payment method.');
    } finally {
      setModalSubmitting(false);
    }
  };

  const handleDeleteCard = async () => {
    if (!removingCard) {
      return;
    }

    try {
      setModalSubmitting(true);
      await deletePaymentMethod(token, removingCard.card_id);
      publishStatus('success', 'Payment method removed successfully.');
      setRemovingCard(null);
      await loadPaymentData();
    } catch (error) {
      const message = error.message || 'Failed to remove payment method.';
      publishStatus('error', message);
    } finally {
      setModalSubmitting(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="payment-method-empty-state is-loading">
          <div className="payment-method-empty-icon" aria-hidden="true">
            <CreditCard size={20} />
          </div>
          <strong>Loading payment methods...</strong>
          <span>Please wait a moment.</span>
        </div>
      );
    }

    if (paymentMethods.length === 0) {
      return (
        <div className="payment-method-empty-state">
          <div className="payment-method-empty-icon" aria-hidden="true">
            <CreditCard size={20} />
          </div>
          <strong>No saved cards yet</strong>
          <span>
            {customerCode
              ? 'Add a card to manage future payments and auto-reload.'
              : 'Add your first card to establish a payment profile for this account.'}
          </span>
        </div>
      );
    }

    return (
      <div className="payment-method-list">
        {paymentMethods.map((card) => (
          <div
            key={card.card_id}
            className={`payment-method-item ${card.is_default ? 'is-default' : ''}`}
          >
            <div className="payment-method-main">
              <div className="payment-method-summary-badge">
                <CreditCard size={18} />
              </div>

              <div className="payment-method-copy">
                <div className="payment-method-copy-top">
                  <h4>{getCardLabel(card)}</h4>
                  {card.is_default && <span className="payment-method-default-pill">Default</span>}
                </div>
                <p>Expires {formatExpiry(card)}</p>
              </div>
            </div>

            {paymentMethods.length > 1 && (
              <div className="payment-method-actions">
                <button
                  type="button"
                  className="payment-method-danger-btn"
                  onClick={() => setRemovingCard(card)}
                  disabled={modalSubmitting}
                >
                  <Trash2 size={16} />
                  Remove
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="payment-methods-card">
        <div className="card-header">
          <h3>
            <span className="payment-method-header-icon" aria-hidden="true">
              <CreditCard size={18} />
            </span>
            Payment Methods
          </h3>

          <button
            type="button"
            className="payment-method-primary-btn"
            onClick={() => setAddModalOpen(true)}
          >
            <PlusCircle size={16} />
            Add Card
          </button>
        </div>

        <div className="payment-methods-content with-list">
          {!customerCode && !loading && (
            <p className="payment-method-inline-note">
              Saving your first card will create the payment profile used for future card updates and removals.
            </p>
          )}

          <div className="payment-methods-list-shell">
            {renderContent()}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isAddModalOpen}
        onClose={() => {
          if (!modalSubmitting) {
            setAddModalOpen(false);
          }
        }}
        title="Add Payment Method"
      >
        <AddPaymentMethodForm
          customerCode={customerCode}
          isSubmitting={modalSubmitting}
          onCancel={() => setAddModalOpen(false)}
          onSubmit={handleAddCard}
        />
      </Modal>

      <Modal
        isOpen={Boolean(removingCard)}
        onClose={() => {
          if (!modalSubmitting) {
            setRemovingCard(null);
          }
        }}
        title="Remove Payment Method"
      >
        <div className="payment-method-delete-copy">
          <p>
            Remove <strong>{removingCard ? getCardLabel(removingCard) : 'this card'}</strong> from your
            account?
          </p>
          <p>This will delete the saved payment method from Helcim.</p>
        </div>

        <div className="payment-method-modal-actions">
          <button
            type="button"
            className="payment-method-secondary-btn"
            onClick={() => setRemovingCard(null)}
            disabled={modalSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="payment-method-danger-btn"
            onClick={handleDeleteCard}
            disabled={modalSubmitting}
          >
            {modalSubmitting ? 'Removing...' : 'Remove Card'}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default PaymentMethods;
